import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Button, Spin, message } from 'antd';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { checkAdminAccess } from '@/apis/account';
import { fetchUserInfo } from '@/apis/user';
import FeaturePage from '@/components/featurepage';

export const PreviousPageContext = createContext('/');

// 覆盖所有页面，记录进入管理页前的站内地址（含查询参数和锚点）。
const AccessBoundary = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated, userInfo, sessionVersion } = useSelector(state => state.user);
  const [previousPage, setPreviousPage] = useState('/');
  const path = `${location.pathname}${location.search}${location.hash}`;
  useLayoutEffect(() => {
    if (!/^\/(?:admin|personal\/(?:admin|statistics|audit|report-management))(?:\/|$)/.test(location.pathname)) setPreviousPage(path);
  }, [location.pathname, path]);
  useEffect(() => {
    if (isAuthenticated && userInfo.id && userInfo.username) dispatch(checkAdminAccess());
  }, [dispatch, isAuthenticated, userInfo.id, userInfo.username, sessionVersion]);
  return <PreviousPageContext.Provider value={previousPage}><Outlet /></PreviousPageContext.Provider>;
};

export const AdminGuard = ({ children, embedded = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const previousPage = useContext(PreviousPageContext);
  const { isAuthenticated, userInfo, sessionVersion, adminAccess, adminAccessError } = useSelector(state => state.user);
  const [checkedSession, setCheckedSession] = useState(null);
  const [retry, setRetry] = useState(0);
  const redirected = useRef(false);
  const sessionKey = `${sessionVersion}:${userInfo.id}`;
  useEffect(() => {
    if (!isAuthenticated || !userInfo.id || !userInfo.username) return;
    let active = true;
    setCheckedSession(null);
    // 每次进入管理页重新校验，管理员身份被撤销后不能使用旧权限进入。
    dispatch(checkAdminAccess()).then(() => {
      if (active) setCheckedSession(sessionKey);
    });
    return () => { active = false; };
  }, [dispatch, isAuthenticated, userInfo.id, userInfo.username, sessionKey, retry]);
  const checked = checkedSession === sessionKey && adminAccess !== 'checking';
  const denied = !isAuthenticated || (checked && adminAccess === 'denied');
  useEffect(() => {
    if (!denied || redirected.current) return;
    redirected.current = true;
    message.warning({ key: 'admin-access-denied', content: '仅管理员可以进入管理页面' });
    // 替换误入的地址，避免浏览器前进时反复进入无权限页面。
    navigate(previousPage, { replace: true });
  }, [denied, navigate, previousPage]);
  if (denied) return null;
  if (checked && adminAccess === 'allowed') return children;
  const missingProfile = !userInfo.id || !userInfo.username;
  return <FeaturePage embedded={embedded} className="adminAccess" title="权限验证">
    <section className="featurePage-panel">
      {missingProfile || (checked && adminAccess === 'error')
        ? <Alert type="error" showIcon title={missingProfile ? '无法获取当前账号资料，请重试' : adminAccessError}
          action={<Button onClick={() => {
            if (missingProfile) dispatch(fetchUserInfo());
            else { setCheckedSession(null); setRetry(value => value + 1); }
          }}>重试</Button>} />
        : <Spin tip="正在验证管理员权限"><div style={{ minHeight: 120 }} /></Spin>}
    </section>
  </FeaturePage>;
};

export default AccessBoundary;
